import { loginByMobile, toLogin, getBindUserInfo, toBindPhone, toChangeBindAccount } from '@/api/user';
import { usePopup } from '@/utils/hooks/usePopup';
import type { AccountInfo } from '@/stores/user';
import { useAccount } from '@/utils/hooks/useAccount';
import { useAccountStore } from '@/stores/user';
import { i18n } from '@/i18n';
import router from '@/router';
import { emitEvent } from '@/utils/eventBus';
import isPhone from '@/utils/isPhone';
import cookie from 'js-cookie';
import type { LoginMobile, LoginPassword } from '@/api/user/types';
import {
  bindAuthMobile,
  getProjectIssuePrInfo,
  createMiniProgramQRCode,
  getMiniProgramQRCodeStatus,
  miniProgramQRCodeLogin
} from '@/api/user';
import type { TriggerType } from './enum';
import { AuthTypeEnum, AuthResType, LoginMethodEnum } from './enum';
import { csdnloginCheck } from '@/utils/csdnLoginCheck';
import { BindStatusEnum } from '@/api/user/types';
// import { useAccModal } from '@/utils/hooks/useAccModal';
import {
  UTM_SOURCE,
  PASSPORT_PREFIX,
  AUTH_WINDOW,
  OAUTH_LOGIN_TYPE_KEY,
  OAUTH_CANCEL,
  LOGIN_MODAL_TRIGGER_SOURCE,
  REGISTER_SOURCE_TAB
} from '@/constant';
import { isAuthWindow, computeWindowFeatures, removeIsLoginParam } from '@/utils';
import keys from 'lodash/keys';
import { useReport } from '@/utils/hooks/useReport';
import { transformUrl } from '@/utils/tools';
import { VITE_API_HOST, VITE_COOKIE_DOMAIN } from '@/utils/tools';
import { collectRegisterLoginEvent } from '@/utils/eventCollectors';
import type { CaptchaConfig } from '@/components/LoginModal/types';

// const { closeModal, isMountedModal } = useAccModal();
const cookieDomain = VITE_COOKIE_DOMAIN();

type ConfigParams = {
  /** 登录方式 */
  type?: AuthTypeEnum;
  responseType?: `${AuthResType}`;
  /** 绑定状态 */
  bindStatus?: BindStatusEnum;
  triggerType?: TriggerType;
  /** 绑定失败提示信息 */
  failMsg?: string;
  /** 错误信息 */
  errorInfo?: string;
  // 用户信息
  userInfo?: Required<AccountInfo>;
  /** 自定义跳转地址，场景如：atomgit、微信后端主动授权 */
  path?: string;
  /** 仅关闭，无提示 */
  noTip?: boolean;
  needRedirect?: Boolean; // 登录之后是否需要跳转
  validFunc?: Function;
  successFunc?: Function;
  failFunc?: Function;
  showMfaCheck?: Function;
  country?: string;
  loginMethod?: LoginMethodEnum;
  loginDeviceType?: string;
  captchaType?: CaptchaConfig;
};

type commonLoginParams = {
  type: AuthTypeEnum;
  validFunc?: Function;
  triggerType?: TriggerType;
  needRedirect?: Boolean; // 登录之后是否需要跳转
};

interface PhoneLoginParams {
  param: ConfigParams;
  loginParams: LoginMobile;
}

interface PasswordLoginParams {
  param: ConfigParams;
  loginParams: LoginPassword;
  graphValidate: Function;
  lintRemoteLogin?: Function;
  yunpianGraphValidate: Function;
  yiDunGraphValidate: Function;
}

interface BindPhoneLoginParams {
  param: ConfigParams;
  loginParams: { mobile: string; verification_code: string; mask: string };
}

interface ChangeBindAccountLoginParams {
  param: ConfigParams;
  loginParams: { mask: string };
}

interface IBindStatusParams {
  data: {
    user_id: string;
    username: string;
    mask: string;
    identity_id: string;
    mobile: string;
    verificationcode: string;
    signup_type: string;
    platform: string;
  };
}

interface TGetBindInfo {
  platform: string | string[];
  code: string | string[];
  state: string | string[];
  signup_type: string;
}
type csdnQuickLoginParams = {
  route: Record<string, any>;
};

const { t } = i18n.global;
// 图形验证码接口code
export const GRAPH_CODE = 400017;
export const GRAPH_CODE_01 = 400007; // 只代表验证码获取错误，不再触发图形验证码
// 云片验证码接口 code
// 改动：不再靠错误码来区分验证码供应商，而是靠 captchaType.provider 来区分
// 但还是要靠这两个错误码，来判断是否需要触发行为验证码
export const YUNPIAN_GRAPH_CODE = 400018;

export const needGraphCode = (errorCode: number | undefined) => {
  if (!errorCode) return false;
  return [GRAPH_CODE, YUNPIAN_GRAPH_CODE].includes(errorCode);
};

export type captchaModeType = 'tianai' | 'yunpian' | 'yidun';
// 异地登录接口 code
const REMOTE_LOGIN_CODE = 400015;
// 保存最大登录过的手机号数量
const MAX_MOBILE_COUNT = 10;

const LoginConfig = {
  // 短信登录
  async phone({ param, loginParams }: PhoneLoginParams) {
    const { validFunc, successFunc = () => {}, failFunc = () => {}, country = '' } = param;
    // 校验表单和协议
    const check = validFunc ? await validFunc() : true;
    if (!check) return;
    // 登录接口
    const result = await loginByMobile(loginParams);

    if (!result.error) {
      const resultData = result.data.data;
      saveLoginMobile(loginParams.mobile, country);
      // 登录成功,UI回显
      successFunc(resultData, loginParams.mobile);
      // 登录成功后跳转逻辑
      phoneAndPasswordLogin(resultData, LoginMethodEnum.PHONE);
    } else {
      // 登录失败
      failFunc(result.error.error_message);
      collectRegisterLoginEvent({ isRegister: false, registerLoginMethod: LoginMethodEnum.PHONE, success: false });
    }
  },
  // 密码登录
  async password({
    param,
    loginParams,
    graphValidate,
    lintRemoteLogin,
    yunpianGraphValidate,
    yiDunGraphValidate
  }: PasswordLoginParams) {
    const { validFunc, successFunc = () => {}, failFunc = () => {}, captchaType } = param;
    // 校验表单和协议
    const check = validFunc ? await validFunc() : true;
    if (!check) return;
    // 登录接口
    const result = await toLogin(loginParams);

    // 触发图形验证码（手机号登录在点击获取验证码时判断）
    if (needGraphCode(result?.error?.error_code) && captchaType?.provider === 'SELF') {
      graphValidate();
      return;
    }

    if (needGraphCode(result?.error?.error_code) && captchaType?.provider === 'YUNPIAN') {
      yunpianGraphValidate();
      return;
    }

    if (needGraphCode(result?.error?.error_code) && captchaType?.provider === 'YIDUN') {
      yiDunGraphValidate();
      return;
    }

    if (lintRemoteLogin && result?.error?.error_code === REMOTE_LOGIN_CODE) {
      lintRemoteLogin();
      return;
    }

    if (!result.error) {
      const resultData = result.data.data;
      const { user_status_enum } = resultData;
      // 登录信息验证成功， UI回显（如开启了MFA，需验证）
      await successFunc(resultData);
      // MFA认证通过，刷新路由
      if (!['EMPTY_MOBILE', 'MFA_CHECK'].includes(user_status_enum)) {
        // 登录成功后跳转逻辑
        phoneAndPasswordLogin(resultData, LoginMethodEnum.PASSWORD);
      }
    } else {
      failFunc({ errorMessage: result.error.error_message });
      collectRegisterLoginEvent({ isRegister: false, registerLoginMethod: LoginMethodEnum.PASSWORD, success: false });
    }
  },
  // 未绑定手机号的账号，进行手机号码绑定操作
  async bindPhone({ param, loginParams }: BindPhoneLoginParams) {
    const { successFunc = () => {}, failFunc = () => {} } = param;
    const result = await toBindPhone(loginParams);

    if (!result.error) {
      const resultData = result.data.data;
      const { mask, username, user_status_enum } = resultData;
      if (username && user_status_enum === 'EMPTY_MOBILE') {
        // 手机号被占用，需要提示用户
        failFunc({ isConflict: true, errorMessage: '手机号被占用', mask, occupiedUsername: username });
        return;
      }
      // 登录信息验证成功， UI回显（如开启了MFA，需验证）
      await successFunc(resultData);
      // MFA认证通过，刷新路由
      if (!['MFA_CHECK'].includes(user_status_enum)) {
        // 登录成功后跳转逻辑
        phoneAndPasswordLogin(resultData, LoginMethodEnum.PASSWORD);
      }
    } else {
      failFunc({ errorMessage: result.error.error_message });
    }
  },
  // 手机号码绑定冲突，用户选择更换绑定账号
  async changeBindAccount({ param, loginParams }: ChangeBindAccountLoginParams) {
    const { successFunc = () => {}, failFunc = () => {} } = param;
    const result = await toChangeBindAccount(loginParams);

    if (!result.error) {
      const resultData = result.data.data;
      const { user_status_enum } = resultData;
      // 登录信息验证成功， UI回显（如开启了MFA，需验证）
      await successFunc(resultData);
      // MFA认证通过，刷新路由
      if (!['MFA_CHECK'].includes(user_status_enum)) {
        // 登录成功后跳转逻辑
        phoneAndPasswordLogin(resultData, LoginMethodEnum.PASSWORD);
      }
    } else {
      failFunc(result.error.error_message);
    }
  },
  // 小程序登录 - 获取二维码
  async miniProgramGetQrCode() {
    return await createMiniProgramQRCode({ platform: 'wechat_mini_program' });
  },

  // 小程序登录 - 检查状态
  async miniProgramCheckStatus(scene_id: string) {
    return await getMiniProgramQRCodeStatus({
      platform: 'wechat_mini_program',
      scene_id
    });
  },

  // 小程序登录 - 发起登录
  async miniProgramLogin(scene_id: string) {
    const result = await miniProgramQRCodeLogin({
      platform: 'wechat_mini_program',
      scene_id
    });

    if (!result.error) {
      collectRegisterLoginEvent({
        isRegister: result.data.data.is_new,
        registerLoginMethod: LoginMethodEnum.MINI_PROGRAM,
        success: true
      });
      // 登录成功后跳转逻辑
      phoneAndPasswordLogin(result.data.data, LoginMethodEnum.MINI_PROGRAM);
      return { success: true, data: result.data.data };
    } else {
      collectRegisterLoginEvent({
        isRegister: false,
        registerLoginMethod: LoginMethodEnum.MINI_PROGRAM,
        success: false
      });
    }

    return { success: false, error: result.error };
  },
  // csdn授权前(pr和mobile)
  beforeCsdnLogin(param: commonLoginParams) {
    const { type, validFunc } = param;
    const check = validFunc ? validFunc() : true;
    if (!check) return;
    saveStatusBeforeLogin(param);
    goAuthPage(type);
  },
  // csdn授权前(携带isLogin)
  async beforeCsdnQuickLogin(param: csdnQuickLoginParams) {
    const { route } = param;
    const loginCallback = () => {
      saveLoginReturnUrl(location.href);
      saveSignPath();
      removeLoginTriggerSourceCookie(); // 删除登录来源cookie
      goAuthPage(AuthTypeEnum.CSDN, true);
    };
    return await csdnloginCheck(route, loginCallback);
  },
  // csdn授权后
  afterCsdnLogin(param: ConfigParams) {
    bindResultResolve(param);
  },
  // atomgit授权后
  afterAtomGitLogin(param: ConfigParams) {
    bindResultResolve(param);
  },
  // 微信授权前
  async beforeWechatLogin(param: commonLoginParams) {
    const { validFunc, type } = param;
    const check = validFunc?.(); // 需要同意授权
    if (!check) return;
    saveStatusBeforeLogin(param); // 登录前的状态保存
    goAuthPage(type); // 打开授权页面
  },
  // 微信授权后
  afterWechatLogin(param: ConfigParams) {
    bindResultResolve(param);
  },
  // 其他三方授权前（gitee、github、hbuilder）
  beforeOtherLogin(param: commonLoginParams) {
    // 已登录处理
    // pc phone 区分
    const { type, validFunc } = param;
    const check = validFunc ? validFunc() : true;
    if (!check) return;

    saveStatusBeforeLogin(param);

    goAuthPage(type);
  },
  // 其他三方授权后
  afterOtherLogin(param: ConfigParams) {
    bindResultResolve(param);
  }
};

// 获取第三方授权信息
async function getOauthInfo(param: TGetBindInfo, csdnBlogSource: string) {
  const { platform, code, state, signup_type } = param;
  // 获取cookie中回调地址,传给后端区分注册来源（ai社区）
  const returnUrl = getCookieLoginReturnUrl();
  return await getBindUserInfo({ platform, code, state, signup_type }, returnUrl, csdnBlogSource);
}

/**
 * 登录前的状态保存
 */
function saveStatusBeforeLogin(param: commonLoginParams) {
  const { triggerType, needRedirect } = param;
  removeUtmSource();
  if (needRedirect) {
    saveLoginReturnUrl();
  }
  saveSignPath();
  saveLoginType(triggerType);
}

/**
 * 保存登录前页面地址
 */
function saveLoginReturnUrl(url: string = '') {
  const returnUrl = url || (router.currentRoute.value.query?.returnUrl as string) || '';
  localStorage.setItem('loginReturnUrl', returnUrl);
}

/**
 * 保存注册上报ref
 */
function saveSignPath() {
  localStorage.setItem('sign_path', location.href);
}

/**
 * 保存注册上报类型
 */
function saveLoginType(triggerType: TriggerType = '') {
  sessionStorage.setItem('loginType', triggerType);
}

/**
 * 保存当前 oauth 三方登录 type
 */
function saveOauthType(oauthType = '') {
  sessionStorage.setItem(OAUTH_LOGIN_TYPE_KEY, oauthType);
}

/**
 * 获取当前 oauth 三方登录 type
 */
function getOauthType() {
  return sessionStorage.getItem(OAUTH_LOGIN_TYPE_KEY);
}

/**
 * 存储当前登录弹窗-触发入口
 */
function setLoginTriggerSource(triggerSource: string) {
  sessionStorage.setItem(LOGIN_MODAL_TRIGGER_SOURCE, triggerSource);
}

/**
 * 获取当前登录弹窗-触发入口
 */
function getLoginTriggerSource() {
  return sessionStorage.getItem(LOGIN_MODAL_TRIGGER_SOURCE) || cookie.get(LOGIN_MODAL_TRIGGER_SOURCE);
}

/**
 * 获取注册来源 tab 值
 */
function getRegisterSourceTab() {
  return sessionStorage.getItem(REGISTER_SOURCE_TAB);
}

/**
 * 获取注册来源和登录来源，用逗号拼接
 * @returns
 */
function getFullStepSource() {
  const loginTriggerSource = getLoginTriggerSource();
  const registerSource = getRegisterSourceTab();
  if (loginTriggerSource && registerSource) {
    return `${loginTriggerSource},${registerSource}`;
  }
  return registerSource || loginTriggerSource;
}

/** 移除注册来源 */
function removeUtmSource() {
  localStorage.removeItem(UTM_SOURCE);
}

/**
 * 获取回跳地址
 */
function getLoginReturnUrl() {
  return cookie.get('third_utm_source_sign_path') || localStorage.getItem('loginReturnUrl') || '/';
}

/**
 * 获取cookie中回调地址
 */
function getCookieLoginReturnUrl() {
  return cookie.get('third_utm_source_sign_path') || '';
}

/** 设置邮箱是否有效 */
function setEmailValid(username: string, email: string) {
  if (`${username}@gitcode.com` === email) {
    // 需要修改默认邮箱的弹窗
    localStorage.setItem('validator_email', 'invalid');
  } else {
    localStorage.setItem('validator_email', 'valid');
  }
}

/**
 * 打开授权页面
 * @param {boolean} - noOpen 不打开子窗口
 */
function goAuthPage(type: AuthTypeEnum, noOpen: boolean = false) {
  let url = `${VITE_API_HOST()}${PASSPORT_PREFIX || ''}/api/v1/oauth/login/${type}`;

  if (type === AuthTypeEnum.CSDN) {
    // 如果是从csdn跳转进入的授权登陆，需拼接namespace=getcsdnpassportuv 字段
    const utm_source = '?namespace=getcsdnpassportuv';
    url = `${url}${utm_source}`;
  }

  if (isPhone() || noOpen) {
    location.href = url;
  } else {
    const windowFeatures = computeWindowFeatures();
    window.open(url, AUTH_WINDOW, windowFeatures);
  }
}

/** 处理绑定结束后的处理*/
function bindResultResolve(params: ConfigParams) {
  const { responseType } = params;
  if (responseType === AuthResType.CANCEL) {
    cancelAuth(params);
  } else if (responseType === AuthResType.SUCCESS) {
    // 获取 绑定信息 成功
    successResolve(params);
  } else if (responseType === AuthResType.FAIL) {
    // 失败
    failResolve(params);
  }
}

// 抛出消息
function sendMessageToParent(message: any) {
  window.parent && window.parent.postMessage(message, '*');
}

/**
 * 手机号或密码登录成功后的统一处理逻辑
 * @param userInfo - 登录成功后返回的用户信息，必须包含 access_token、refresh_token 等
 * @param loginMethod - 登录方式（枚举值，区分手机号/密码等登录方式）
 * @param loginType - 登录类型（可选，用于区分不同登录场景，如第三方登录等）
 * @param isRegister - 是否为注册流程（默认为 false，区分注册/登录埋点）
 */
function phoneAndPasswordLogin(
  userInfo: Required<AccountInfo>,
  loginMethod: LoginMethodEnum,
  loginType: string = '',
  isRegister: boolean = false
) {
  // 埋点：记录注册/登录事件
  collectRegisterLoginEvent({
    isRegister,
    registerLoginMethod: loginMethod,
    success: true
  });

  // 登录成功后，执行后续处理（如关闭弹窗、跳转、保存用户信息等）
  successResolve(
    {
      path: (router.currentRoute.value?.query?.returnUrl as string) || '',
      userInfo,
      type: AuthTypeEnum.PHONE_AND_PASSWORD,
      loginMethod
    },
    loginType
  );

  // 构造需要传递给父窗口的参数（如 access_token、refresh_token）
  const params: any = {
    access_token: userInfo.access_token,
    refresh_token: userInfo.refresh_token
  };

  // 如果有 loginType，附加到参数中
  if (loginType) {
    params.loginType = loginType;
  }

  // 向父窗口发送登录成功消息（适用于 iframe、弹窗等场景）
  sendMessageToParent(params);
}

async function checkProjectIssuePrInfo() {
  const res = await getProjectIssuePrInfo();
  const { accountInfo } = useAccountStore();
  const { created_issue, created_pr, created_project } = res.data.data || {};
  const haveRepositoryInfo = created_issue || created_pr || created_project;
  localStorage.setItem('repository_user', accountInfo.username);
  localStorage.setItem('project_issue_pr_info', haveRepositoryInfo);
  localStorage.setItem('project_issue_pr_info_time', new Date().getTime().toString());
  return haveRepositoryInfo;
}

async function redirectToDashboardIfNeeded() {
  const haveRepositoryInfo = await checkProjectIssuePrInfo();
  if (haveRepositoryInfo && (import.meta as any).env.VITE_HOME_LOGIN_NEED_REDIRECT === 'dashboard') {
    window.location.replace('/dashboard');
    return true;
  }
  return false;
}

/** 绑定成功后的处理 */
async function successResolve(params: ConfigParams, loginType: string = '') {
  const { path, userInfo, type, loginMethod } = params;
  if (!path) {
    // 如果没有回调地址 回调地址中就不需要挂载loginType参数了
    loginType = '';
  }
  // 保存用户信息
  if (userInfo) {
    const { RecordInfo } = useAccount();
    // 头像地址处理
    const transformUserInfo = {
      ...userInfo,
      avatar: transformUrl(userInfo?.avatar)
    };
    RecordInfo(transformUserInfo);
    const { username, email } = transformUserInfo;
    setEmailValid(username, email); // 邮箱无效，弹出提示
  }

  saveLastLoginType(loginMethod as LoginMethodEnum);
  // 删除登录来源cookie
  removeLoginTriggerSourceCookie();

  /* 模态框处理（pc端手机号或密码登录成功，需要关闭模态框刷新当前页面） */
  if (type === AuthTypeEnum.PHONE_AND_PASSWORD) {
    const { unMount, isMounted } = usePopup();
    if (isMounted()) {
      unMount();
      return;
    }
  }

  /* 子窗口处理，关闭小窗 */
  if (isAuthWindow()) {
    window.opener?.postMessage({ type: AuthResType.SUCCESS, userInfo }, '*');
    window?.close();
    return;
  }

  /* 非子窗口处理 */
  DMessage.success({
    message: t('common.tips.welcome'),
    onClose: () => {
      emitEvent('notice');
    }
  });
  handleReturnUrl(path as string, loginType);
}

// 特殊处理快应用，已登录后唤起登录弹窗，直接关闭弹窗
function handleQuickAppLogin(userInfo: { access_token: string; refresh_token: string }) {
  if (isAuthWindow()) {
    window.opener?.postMessage(
      {
        type: AuthResType.SUCCESS,
        userInfo: {
          access_token: userInfo.access_token,
          refresh_token: userInfo.refresh_token
        }
      },
      '*'
    );
    window?.close();
    return;
  } else {
    router.replace('/');
  }
}

/** 处理 returnUrl 场景 */
async function handleReturnUrl(path?: string, loginType: string = '') {
  // 之前设置的返回地址
  const returnUrl = path || getLoginReturnUrl();
  console.log('handleReturnUrlLog');
  // 移除保存的回跳地址信息
  removeStatusAfterLogin();
  if (returnUrl && returnUrl.includes(window.location.origin)) {
    const { pathname, search } = (() => {
      try {
        return new URL(returnUrl);
      } catch (e) {
        return { pathname: '', search: '' };
      }
    })();
    const routeName = router.resolve(pathname)?.name;
    const isHomePage = routeName === 'home';
    if (isHomePage && !search) {
      console.log('handleReturnUrlDashboard');
      const redirected = await redirectToDashboardIfNeeded();
      if (redirected) return;
    }
  }
  defaultBackResolve(returnUrl, loginType);
}

/** 取消/拒绝授权-上报 */
function reportCancelAuth() {
  const currentAuthType = getOauthType();
  currentAuthType && useReport(OAUTH_CANCEL, { source: currentAuthType });
}

/** 取消授权 */
function cancelAuth(params: ConfigParams) {
  const { noTip = false } = params;
  reportCancelAuth();

  /* 子窗口处理 */
  if (isAuthWindow()) {
    // 你已取消授权
    !noTip && window.opener?.postMessage({ type: AuthResType.FAIL, message: t('oauth.revokedAuthorization') }, '*');
    window?.close();
    return;
  }

  /* 非子窗口处理 */
  const returnUrl = getLoginReturnUrl();
  // 登录成功后清除信息
  removeStatusAfterLogin();
  defaultBackResolve(returnUrl);
}

function failResolve(params: ConfigParams) {
  const { errorInfo } = params;
  const msg = errorInfo || t('userSetting.tips.autFai');
  reportCancelAuth();

  /* 子窗口处理 */
  if (isAuthWindow()) {
    window.opener?.postMessage({ type: AuthResType.FAIL, message: msg }, '*');
    window?.close();
    return;
  }

  /* 非子窗口处理 */
  DMessage.warning(msg);
  const returnUrl = getLoginReturnUrl();
  // 登录成功后清除信息
  removeStatusAfterLogin();
  defaultBackResolve(returnUrl);
}

/** 登录成功后清除信息（如： 跳转地址） */
function removeStatusAfterLogin() {
  localStorage.removeItem('loginReturnUrl'); // 本站跳转地址
  cookie.set('third_utm_source_sign_path', '', { expires: -1, domain: cookieDomain }); // 其他域跳转地址（ai）
}

/** 清除登录来源cookie */
function removeLoginTriggerSourceCookie() {
  cookie.set(LOGIN_MODAL_TRIGGER_SOURCE, '', { expires: -1, domain: cookieDomain });
}

/** 默认回跳操作处理 */
function defaultBackResolve(url: string, loginType: string = '') {
  // let redirectPath = '';
  // redirectPath = url.replace(/[\?&]isLogin=\w+/g, ''); // 去掉isLogin参数
  // redirectPath = redirectPath.replace(location.origin, '') || '/'; // 去掉域名，保留路径，便于使用router跳转
  // TODO: 某些页面（如项目首页）未更新，修复后可使用使用router跳转
  let newUrl = removeIsLoginParam(url);
  if (loginType) {
    if (window.location.origin === import.meta.env.VITE_AI_HOST) {
      const url = new URL(newUrl);
      url.searchParams.set('aiRegist', '1');
      newUrl = url.toString();
    } else {
      // 如果有utm_source参数 则不需要二次encode，直接拼接
      if (new URL(newUrl).searchParams.has('utm_source')) {
        newUrl += `&loginType=${loginType}`;
      } else {
        newUrl += `%26loginType%3d${loginType}`;
      }
    }
  }
  // 火山上报需要延迟跳转，暂定500ms
  setTimeout(() => {
    location.replace(newUrl);
  }, 500);
  // location.replace(newUrl);
}

/**
 * 处理登录弹框发送消息给主应用的处理
 *  */
const HandleParentWindowGetLoginInfo = {
  [AuthResType.SUCCESS]: () => {
    // 移除模态框
    // if (isMountedModal()) {
    //   closeModal();
    // }
    // 关闭登录框
    const { unMount, isMounted } = usePopup();
    if (isMounted()) {
      unMount();
    }

    // 处理 returnUrl 场景
    const returnUrl = getLoginReturnUrl();
    // PC 端，有返回地址，且路由为 /login (oauth 授权页)，则需要处理回跳逻辑
    if (!isPhone() && returnUrl && router.currentRoute.value.path === '/login') {
      handleReturnUrl();
      return;
    }
  },
  [AuthResType.FAIL]: (event: MessageEvent<any>) => {
    reportCancelAuth();
    DMessage.warning(event.data.message || '授权失败');
  }
};

// 关于登录流程中操作调用的接口
const OperateLoginConfig = {
  // 获取绑定、换绑、注册的状态
  async getBindStatus(param: IBindStatusParams) {
    const { data } = param;
    const {
      user_id = '',
      username = '',
      mask = '',
      identity_id = '',
      mobile = '',
      verificationcode = '',
      signup_type = '',
      platform = ''
    } = data;
    return await bindAuthMobile({
      user_id,
      username,
      mask,
      identity_id,
      mobile,
      verificationcode,
      signup_type,
      platform
    });
  }
};

/**
 * 保存登录过的手机号和country
 */
function saveLoginMobile(mobile: string, country: string) {
  // 获取存储的登录过的手机号数组
  const loginMobileList = JSON.parse(localStorage.getItem('loginMobileList') || '[]') as {
    mobile: string;
    country: string;
  }[];
  // 将手机号放在第一位，如果超过10个，则删除最后一个，如果之前有就放在第一个
  const index = loginMobileList.findIndex((item) => item.mobile === mobile);
  if (index !== -1) {
    loginMobileList.splice(index, 1);
  }
  loginMobileList.unshift({ mobile, country });
  if (loginMobileList.length > MAX_MOBILE_COUNT) {
    loginMobileList.pop();
  }
  localStorage.setItem('loginMobileList', JSON.stringify(loginMobileList));
}

/**
 * 保存最近登录方式
 */
function saveLastLoginType(loginMethod: LoginMethodEnum) {
  let currentLoginType = loginMethod as string;
  // 区分是否鸿蒙登录
  if (loginMethod === LoginMethodEnum.GITEE) {
    const harmonyLogin = localStorage.getItem('harmonyLogin');
    if (harmonyLogin) {
      currentLoginType = LoginMethodEnum.HARMONY;
    }
  }
  // 删除鸿蒙登录标识
  localStorage.removeItem('harmonyLogin');
  localStorage.setItem('lastLoginMethod', currentLoginType);
}

export {
  LoginConfig,
  OperateLoginConfig,
  getOauthInfo,
  phoneAndPasswordLogin,
  saveLoginReturnUrl,
  saveSignPath,
  saveLoginType,
  saveOauthType,
  getOauthType,
  setLoginTriggerSource,
  getLoginTriggerSource,
  getRegisterSourceTab,
  getFullStepSource,
  goAuthPage,
  HandleParentWindowGetLoginInfo,
  saveLoginMobile,
  saveLastLoginType,
  handleQuickAppLogin,
  getLoginReturnUrl
};
